import React, { useState, useEffect, useRef } from 'react';
import { Bot, Send, User, Loader2, Sparkles } from 'lucide-react';
import { getRegulatoryAdvice } from '../services/geminiService';
import { SAMPLE_PROMPTS } from '../constants';

interface Message {
  id: string;
  role: 'user' | 'ai';
  content: string;
  timestamp: Date;
}

export const GeminiAdvisor: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'intro',
      role: 'ai',
      content: 'Hello. I am the RegulatorySherpas AI Assistant. I can help you navigate regulatory pathways for FDA, EMA, and other health authorities. How can I assist your strategy today?',
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    const responseText = await getRegulatoryAdvice(input);

    const aiMessage: Message = {
      id: (Date.now() + 1).toString(),
      role: 'ai',
      content: responseText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, aiMessage]);
    setIsLoading(false);
  };

  const handlePromptClick = (prompt: string) => {
    setInput(prompt);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 h-[calc(100vh-64px)] flex flex-col">
       <div className="mb-6">
         <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
           <Sparkles className="w-6 h-6 text-purple-600" />
           Regulatory Strategy Assistant
         </h1>
         <p className="text-slate-500">Powered by Gemini 2.5 Flash • Ask about guidelines, pathways, and precedents.</p>
       </div>

       <div className="flex-grow bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden flex flex-col">
          {/* Chat Area */}
          <div className="flex-grow overflow-y-auto p-6 space-y-6 scrollbar-hide">
            {messages.map((msg) => (
              <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`flex max-w-[80%] gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${msg.role === 'user' ? 'bg-slate-800' : 'bg-purple-100'}`}>
                    {msg.role === 'user' ? <User className="w-5 h-5 text-white" /> : <Bot className="w-5 h-5 text-purple-600" />}
                  </div>
                  <div className={`p-4 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    msg.role === 'user' 
                      ? 'bg-slate-800 text-white rounded-tr-none' 
                      : 'bg-slate-50 text-slate-800 border border-slate-100 rounded-tl-none'
                  }`}>
                    {msg.content}
                  </div>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                 <div className="flex max-w-[80%] gap-3 flex-row">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center bg-purple-100">
                      <Bot className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl rounded-tl-none flex items-center">
                      <Loader2 className="w-5 h-5 text-slate-400 animate-spin mr-2" />
                      <span className="text-slate-500 text-sm">Analyzing regulatory guidelines...</span>
                    </div>
                 </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 bg-slate-50 border-t border-slate-200">
             {messages.length < 3 && (
               <div className="mb-4 flex flex-wrap gap-2">
                 {SAMPLE_PROMPTS.map((p, i) => (
                   <button
                    key={i}
                    onClick={() => handlePromptClick(p)}
                    className="text-xs bg-white border border-slate-300 text-slate-600 px-3 py-1.5 rounded-full hover:bg-purple-50 hover:text-purple-700 hover:border-purple-200 transition-colors"
                   >
                     {p}
                   </button>
                 ))}
               </div>
             )}
             <div className="relative">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder="Ask about regulatory pathways, guidelines, or strategy..."
                  className="w-full pl-4 pr-12 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none shadow-sm"
                  rows={1}
                  style={{ minHeight: '50px' }}
                />
                <button 
                  onClick={handleSend}
                  disabled={isLoading || !input.trim()}
                  className="absolute right-2 bottom-2 p-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Send className="w-4 h-4" />
                </button>
             </div>
             <p className="text-xs text-slate-400 mt-2 text-center">
               AI can make mistakes. Verify important regulatory info with official health authority guidance.
             </p>
          </div>
       </div>
    </div>
  );
};
